import { FC, ReactNode, useState } from "react";
import {
    Button,
    Card,
    Dropdown,
    MenuProps,
    Modal,
    Tag,
    Tooltip,
    message,
} from "../../../design-system";
import { ArrowRightOutlined, DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { capitalize } from "../../../common/String";
import {
    Project,
    ProjectAction,
    ProjectStatus,
    canDo,
    isRfxProject,
    getProjectTypeLabel,
    getStatus,
} from "../../../types/ProjectTypes";
import { AnalysisWidget } from "./AnalysisWidget";
import { DocumentWidget } from "./DocumentWidget";
import { ProjectNameWidget } from "./ProjectNameWidget";
import {
    deleteProject,
    reassignProjectOwner,
} from "../../../common/api/ProjectClient";

import { handleError } from "../../../common/ErrorHandling";
import { DateTime } from "../../../common/components/DateTime";
import { OwnerCell } from "../../../common/components/OwnerCell";
import { CollaboratorsWidget } from "./CollaboratorsWidget";
import { ProjectTimeline } from "./ProjectTimeline";
import { useUserState } from "../../../common/UserContext";
import { VendorLogo } from "./VendorLogo";
import styles from "./ProjectCard.module.less";

interface ProjectCardProps {
    project: Project;
    selectedVersion?: number;
    onAnalysisSelect: (version: number) => void;
    onAnalysisUpdate?: () => void;
    onUpdate: () => void;
    showActions?: boolean;
    adminActionsMenuItems?: MenuProps["items"];
    noMarginBottom?: boolean;
}

export const ProjectCard: FC<ProjectCardProps> = ({
    project,
    selectedVersion,
    onAnalysisSelect,
    onAnalysisUpdate,
    onUpdate,
    showActions = false,
    adminActionsMenuItems,
    noMarginBottom = false,
}) => {
    const navigate = useNavigate();
    const { me } = useUserState();
    const [timelineOpen, setTimelineOpen] = useState(false);

    const { analyses, documents } = project;
    const orderForm = project.orderForm;
    const vendorName = orderForm?.vendor?.name || orderForm?.vendorKey || "";
    const vendorHero = orderForm?.vendor?.hero;
    const supplierId = orderForm?.vendor?.supplier?.id;
    const ownerName = project.owner?.name;
    const projectStatus = getStatus(project);

    const handleDeleteProject = () => {
        Modal.confirm({
            title: "Delete project",
            content: `Are you sure you want to delete "${project.name || "this project"}"?`,
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteProject(project.id);
                    message.success("Project deleted");
                    navigate("/project");
                } catch (error) {
                    handleError(error);
                }
            },
        });
    };

    const buildActionButtons = (): ReactNode[] => {
        if (!showActions) return [];

        const buttons: ReactNode[] = [
            <CollaboratorsWidget
                key="collaborators"
                project={project}
                currentUserId={me.id}
                onUpdate={onUpdate}
            />,
        ];

        if (canDo(project, ProjectAction.EDIT)
            && project.status !== ProjectStatus.DRAFT
            && project.status !== ProjectStatus.COMPLETED) {
            buttons.push(
                <Button key="update" type="primary" onClick={onAnalysisUpdate}>
                    Update
                </Button>,
            );
        }

        if (project.status === ProjectStatus.DRAFT && canDo(project, ProjectAction.DELETE)) {
            buttons.push(
                <Tooltip key="delete" title="Delete project">
                    <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleDeleteProject}
                    />
                </Tooltip>,
            );
        }

        if (adminActionsMenuItems && adminActionsMenuItems.length > 0) {
            buttons.push(
                <Dropdown key="admin" menu={{ items: adminActionsMenuItems }} trigger={["click"]}>
                    <Tooltip title="Admin actions">
                        <Button icon={<SettingOutlined />} />
                    </Tooltip>
                </Dropdown>,
            );
        }

        return buttons;
    };

    const actionButtons = buildActionButtons();

    return (
        <Card
            className={`${styles.headerCard} ${styles.headerCardWithLogo} ${isRfxProject(project) ? styles.headerCardRfx : ""} ${noMarginBottom ? styles.headerCardNoMarginBottom : ""}`}
        >
            <VendorLogo vendorHero={vendorHero} vendorName={vendorName} projectName={project.name} />
            <div className={styles.headerContent}>
                <div className={styles.fieldRow}>
                    <div className={styles.primaryField}>
                        <div className={styles.fieldLabel}>
                            {capitalize(getProjectTypeLabel(project.type))}
                        </div>
                        <div className={styles.fieldValue}>
                            <ProjectNameWidget
                                projectId={project.id}
                                name={project.name}
                                vendorName={vendorName || undefined}
                                supplierId={supplierId}
                                canEdit={canDo(project, ProjectAction.EDIT)}
                                onUpdate={onUpdate}
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Analysis</div>
                        <div className={styles.fieldValue}>
                            <AnalysisWidget
                                project={project}
                                selectedVersion={selectedVersion}
                                canEdit={canDo(project, ProjectAction.EDIT)}
                                onAnalysisSelect={onAnalysisSelect}
                                onAction={onAnalysisUpdate}
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Status</div>
                        <div className={styles.fieldValue}>
                            <Tag color={projectStatus.color}>{projectStatus.label}</Tag>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Documents</div>
                        <div className={styles.fieldValue}>
                            <DocumentWidget
                                documents={documents || []}
                                canEdit={canDo(project, ProjectAction.EDIT) && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.DRAFT}
                                onAction={onAnalysisUpdate}
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Last update</div>
                        <div className={styles.fieldValue}>
                            <button
                                className={styles.timelineButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTimelineOpen(true);
                                }}
                            >
                                <DateTime date={project.lastUpdateDate} showTime={false} />
                                <ArrowRightOutlined className={styles.timelineButtonIcon} />
                            </button>
                            <ProjectTimeline
                                project={project}
                                open={timelineOpen}
                                onClose={() => setTimelineOpen(false)}
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Assigned to</div>
                        <div className={styles.fieldValue}>
                            <div onClick={(e) => e.stopPropagation()}>
                                <OwnerCell
                                    ownerId={project.ownerId}
                                    ownerName={ownerName}
                                    canEdit={canDo(project, ProjectAction.REASSIGN)}
                                    onSave={async (newOwnerId) => {
                                        await reassignProjectOwner(project.id, newOwnerId);
                                    }}
                                    onUpdate={onUpdate}
                                />
                            </div>
                        </div>
                    </div>

                    {actionButtons.length > 0 && (
                        <div className={styles.actionButtons} onClick={(e) => e.stopPropagation()}>
                            {actionButtons}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};
